import {
  createReactRouterV6DataOptions,
  EventEvent,
  ExceptionEventExtended,
  faro,
  getWebInstrumentations,
  initializeFaro,
  LogLevel,
  ReactIntegration,
  TransportItem,
} from '@grafana/faro-react';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { CancelledError } from '@tanstack/react-query';
import { config } from '@terminal/config';
import { matchRoutes } from 'react-router';

const notLocal = config.urls.root !== 'http://localhost:1234';
const stagingOrProd =
  ['staging', 'prod'].includes(config.app.environment) && notLocal;

/**
 * Errors that fire when an in-flight request is torn down (navigation, a logout
 * cache clear, a component unmount) or hits a transient network/CORS blip. They
 * carry no HTTP status and aren't actionable, so we drop them before they reach
 * the backend.
 *
 * Async cancellations reach Faro via the `unhandledrejection` handler, which
 * discards the thrown error and reports only the exception `value` (message) and
 * `type` (name) — `originalError` is undefined on that path. So we match on
 * `value`/`type` (always populated) and only fall back to `originalError` for
 * the synchronous `window.onerror` path, which does preserve it.
 */
const isNonActionableRequestError = ({
  originalError,
  value = '',
  type = '',
}: {
  originalError?: unknown;
  value?: string;
  type?: string;
}) => {
  const error = originalError as
    | { name?: string; message?: string }
    | undefined;
  const msg = value || error?.message || '';
  const name = type || error?.name || '';

  // react-query cancels in-flight queries with a `CancelledError` whose `name`
  // is the generic "Error" — only its message is "CancelledError".
  if (originalError instanceof CancelledError || msg === 'CancelledError') {
    return true;
  }

  // fetch throws AbortError on abort.
  const isCancellation = msg.includes('AbortError') || name === 'AbortError';

  // Network-level failures never reach the server; the browser throws a
  // TypeError with a non-localized, browser-specific message and no status.
  // (Chrome/Edge: "Failed to fetch", Safari: "Load failed",
  // Firefox: "NetworkError when attempting to fetch resource.")
  const isNetworkFailure =
    name === 'TypeError' &&
    ['Failed to fetch', 'Load failed', 'NetworkError when attempting'].some(
      networkErrorMsg => msg.includes(networkErrorMsg)
    );

  return isCancellation || isNetworkFailure;
};

/**
 * PostHog's own network calls are captured by the tracing instrumentation as
 * Faro events. They're internal analytics traffic, not app behaviour, so we
 * drop them to avoid polluting the event stream.
 */
const isInternalAnalyticsEvent = (payload?: EventEvent) => {
  const attributeName = payload?.attributes?.name;

  return (
    !!attributeName &&
    (attributeName.includes(config.posthog.apiHost) ||
      attributeName.includes(config.posthog.uiHost))
  );
};

interface TrackingInitOptions {
  /**
   * Set to false in apps that manage their own React Router instance and don't
   * use withFaroRouterInstrumentation (e.g. Teams). Without this the
   * ReactIntegration patches matchRoutes globally and can corrupt route
   * validation for routers with non-standard nesting.
   */
  reactRouterIntegration?: boolean;
}

const initialise = ({
  reactRouterIntegration = true,
}: TrackingInitOptions = {}) => {
  if (stagingOrProd) {
    initializeFaro({
      url: config.faro.url,
      app: {
        name: 'terminal',
        version: config.app.version,
        environment: config.app.environment,
      },
      batching: {
        itemLimit: 150,
        sendTimeout: 1000,
      },
      instrumentations: [
        ...getWebInstrumentations(),
        new TracingInstrumentation(),
        ...(reactRouterIntegration
          ? [
              new ReactIntegration({
                router: createReactRouterV6DataOptions({
                  matchRoutes,
                }),
              }),
            ]
          : []),
      ],
      preserveOriginalError: true,
      // Noise, not actionable errors:
      // - PostHog drops capture calls client-side when events burst too fast.
      // - Stripe.js fails to load when blocked by ad blockers / flaky networks.
      ignoreErrors: [
        'This capture call is ignored due to client rate limiting',
        'Failed to load Stripe.js',
      ],
      beforeSend: item => {
        if (item.type === 'exception') {
          const { originalError, value, type } = (
            item as TransportItem<ExceptionEventExtended>
          ).payload;

          // prevents sending to backend
          return isNonActionableRequestError({ originalError, value, type })
            ? null
            : item;
        }

        if (item.type === 'event') {
          return isInternalAnalyticsEvent(item.payload as EventEvent)
            ? null
            : item;
        }

        return item;
      },
    });
  }
};

const captureException = (
  exception: Error | unknown,
  context?: Record<string, string>
) => {
  stagingOrProd
    ? faro.api.pushError(exception as Error, {
        context,
      })
    : console.error({
        Logger: exception,
        context,
      });
};

const captureMessage = (message: string, level?: LogLevel) =>
  stagingOrProd
    ? faro.api.pushLog([message], {
        level: level ?? LogLevel.INFO,
      })
    : console.error({ Logger: message });

export const initialiseUser = (id: string) => {
  if (stagingOrProd && faro?.api) {
    faro.api.setUser({
      id,
    });
  }
};

export const Tracking = {
  initialise,
  captureException,
  captureMessage,
  initialiseUser,
};
